/**
 * The task CTA decision tree, implemented exactly as §6 of the integration
 * guide specifies it. Keeping it here — pure, away from the components — means
 * the card, the details modal and any future surface all reach the same
 * conclusion for the same task.
 *
 * On navigation: V2 replaced V1's `navigateTo` enum with
 * `action { type: ROUTE | URL | NONE, value, label, android_route }`.
 * `android_route` is the stable semantic destination (KYC, deposit, trade) and
 * is what a ROUTE resolves to whenever it is set; `value` is the back end's raw
 * web path, used only as the fallback (see `isValidRoute`). Everything
 * downstream consumes `TaskCta`, so the mapping lives in one place.
 */
import {
  ActionTypeValues,
  ActivityStatusValues,
  ClaimStatusValues,
  Task,
  TaskDestination,
  TaskDestinationValues,
  UserTaskStatusValues
} from './types';

export type TaskCta =
  /** Window closed or task inactive — render the button disabled. */
  | { kind: 'DISABLED'; label: string }
  /** POST …/claim. */
  | { kind: 'CLAIM'; label: string }
  /** Completed, but the reward unlocks only when the campaign completes. */
  | { kind: 'LOCKED_BY_CAMPAIGN'; label: string }
  /** Nothing left to do — show the success/disabled state. */
  | { kind: 'DONE'; label: string }
  /**
   * Navigate inside the app.
   *
   * `destination` is the stable semantic target and wins when it is anything
   * but UNSPECIFIED; `value` is the raw web path the back end sent, used only
   * as the fallback. Resolution to an actual route happens at the navigation
   * site, which keeps this module free of route imports.
   */
  | {
      kind: 'ROUTE';
      label: string;
      value: string;
      destination: TaskDestination;
    }
  /** Open `value` in a new tab. */
  | { kind: 'URL'; label: string; value: string }
  /** POST …/complete — only ever reachable when `selfCompletable`. */
  | { kind: 'COMPLETE'; label: string }
  /** System-verified task: no button, just an explanatory line. */
  | { kind: 'NONE'; label: string }
  /** A guest: the personal part of the task needs a login first. */
  | { kind: 'LOGIN'; label: string };

const LABELS = {
  expired: 'پایان یافته',
  claim: 'دریافت جایزه',
  lockedByCampaign: 'در انتظار',
  claimed: 'دریافت شده',
  completed: 'انجام شده',
  doTask: 'انجام چالش',
  autoVerified: 'به صورت خودکار بررسی می‌شود',
  /** Second step of a "visit the link, then confirm" task. */
  confirmVisited: 'عضو شدم',
  login: 'ورود و شروع چالش'
} as const;

/**
 * Where a guest would see their status, progress, tickets or a claim button.
 * Wording from the back end's migration guide.
 */
export const GUEST_LOGIN_PROMPT = 'برای دیدن پیشرفت و دریافت جایزه وارد شوید';

export interface ResolveCtaOptions {
  /**
   * The user already opened this task's URL in the current cycle. Only
   * matters for a self-completable task with a URL action (e.g. "join our
   * Telegram channel"): its button then turns from the link into the
   * confirmation that completes the task.
   */
  linkOpened?: boolean;
  /**
   * Not logged in: the task came from the public route and carries no status,
   * progress or claim state, so nothing may be read from it.
   */
  isGuest?: boolean;
}

/**
 * Label of the confirmation step. `metadata.complete_label` lets an operator
 * word it for the task (e.g. «دنبال کردم»); the default suits joining a
 * channel or group, which is what these tasks are today.
 */
const confirmLabel = (task: Task): string => {
  const custom = task.metadata?.complete_label;
  return typeof custom === 'string' && custom.trim()
    ? custom.trim()
    : LABELS.confirmVisited;
};

/**
 * A raw ROUTE value must be one of this app's own paths, which all start with
 * /fa/. A plain startsWith('/') also let `//host` and `/\host` through, which
 * browsers open as another site.
 */
export const isValidRoute = (value: string): boolean => /^\/fa\//.test(value);

/** A URL value must be absolute http(s) — no javascript:/data: targets. */
export const isValidExternalUrl = (value: string): boolean =>
  /^https?:\/\//i.test(value);

/**
 * Resolves the single action a task's primary button should take.
 *
 * The order of the branches is load-bearing: claimability outranks the task's
 * own CTA, and an inactive window outranks everything.
 */
export const resolveCta = (
  task: Task,
  { linkOpened = false, isGuest = false }: ResolveCtaOptions = {}
): TaskCta => {
  if (task.activityStatus !== ActivityStatusValues.ACTIVE) {
    return { kind: 'DISABLED', label: LABELS.expired };
  }

  // A guest's task has no user state; every branch below reads it.
  if (isGuest) return { kind: 'LOGIN', label: LABELS.login };

  if (task.claim.status === ClaimStatusValues.CLAIMABLE) {
    return { kind: 'CLAIM', label: LABELS.claim };
  }

  if (
    task.rewardLockedByCampaign ||
    task.claim.status === ClaimStatusValues.LOCKED_BY_CAMPAIGN
  ) {
    return { kind: 'LOCKED_BY_CAMPAIGN', label: LABELS.lockedByCampaign };
  }

  if (task.userStatus === UserTaskStatusValues.CLAIMED) {
    return { kind: 'DONE', label: LABELS.claimed };
  }

  if (task.userStatus === UserTaskStatusValues.COMPLETED) {
    return { kind: 'DONE', label: LABELS.completed };
  }

  const { action } = task;
  const label = action?.label || LABELS.doTask;

  if (action?.type === ActionTypeValues.ROUTE) {
    const { destination } = action;
    // A known destination is navigable on its own; otherwise we can only use
    // the raw path, and only if it looks like one.
    if (
      destination !== TaskDestinationValues.UNSPECIFIED ||
      isValidRoute(action.value)
    ) {
      return { kind: 'ROUTE', label, value: action.value, destination };
    }
  }

  if (
    action?.type === ActionTypeValues.URL &&
    isValidExternalUrl(action.value)
  ) {
    // A self-completable task with a link is two steps: open the link, then
    // confirm. Without this the URL branch always won and `/complete` was
    // unreachable, so the button stayed on the link forever.
    if (task.selfCompletable && linkOpened) {
      return { kind: 'COMPLETE', label: confirmLabel(task) };
    }
    return { kind: 'URL', label, value: action.value };
  }

  // Only `selfCompletable` authorizes a complete call. Trade/deposit/KYC/
  // referral tasks are finished by events or meters and would be rejected with
  // TASK_NOT_SELF_COMPLETABLE.
  if (task.selfCompletable) {
    return { kind: 'COMPLETE', label };
  }

  return { kind: 'NONE', label: LABELS.autoVerified };
};

/** True when the CTA should render as a pressable button. */
export const isActionableCta = (cta: TaskCta): boolean =>
  cta.kind === 'CLAIM' ||
  cta.kind === 'ROUTE' ||
  cta.kind === 'URL' ||
  cta.kind === 'COMPLETE' ||
  cta.kind === 'LOGIN';
